import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import EmptyState from "@/components/EmptyState";
import Table, { TableControls, TableFooter, TableHeading, buildDocumentLinkColumn } from "@/components/Table";
import usePagination from "@/hooks/usePagination";
import { toFormat } from "@/lib/dateHelpers";
import { formatRosterUploadProgramLevel } from "./_utils";
import { UNIVERSITY_ROSTER_UPLOADS_QUERY, type UniversityRosterUploadNode } from "./queries";

export type PastRosterUploadRow = {
  id: string;
  filename: string;
  fileUrl: string | null;
  uploadedBy: string;
  uploadedAt: string;
  programLevel: string;
};

function mapRosterUploadRows(nodes: UniversityRosterUploadNode[]): PastRosterUploadRow[] {
  return nodes.map((node) => ({
    id: node.id,
    filename: node.file?.filename || "roster.csv",
    fileUrl: node.file?.url || null,
    uploadedBy: node.uploadedBy?.name || "",
    uploadedAt: node.createdAt,
    programLevel: formatRosterUploadProgramLevel(node.programLevel)
  }));
}

function getColumns(): ColumnDef<PastRosterUploadRow, unknown>[] {
  return [
    buildDocumentLinkColumn<PastRosterUploadRow>({
      accessorKey: "filename",
      header: "File",
      getDocumentUrl: (row) => row.fileUrl,
      getDocumentName: (row) => row.filename,
      size: 280,
      sortType: "text",
      meta: { headerClassName: "hide-header-in-column" }
    }),
    {
      accessorKey: "uploadedAt",
      header: "Uploaded",
      size: 180,
      meta: { sortType: "date" },
      cell: ({ getValue }) => {
        const date = getValue() as string;
        return toFormat(date, "MMM dd, yyyy, h:mm a") || <span className="opacity-20">--</span>;
      }
    },
    {
      accessorKey: "uploadedBy",
      header: "Uploaded By",
      size: 200,
      meta: { sortType: "text" },
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return value || <span className="opacity-20">--</span>;
      }
    },
    {
      accessorKey: "programLevel",
      header: "Program Version",
      size: 160,
      enableSorting: false
    }
  ];
}

export default function UniversityPastRosterUploadsPage() {
  const { universityId } = useParams();
  const { paginationVariables, createHeaderPagination, createFooterPagination } = usePagination();

  const { data, loading, error } = useQuery(UNIVERSITY_ROSTER_UPLOADS_QUERY, {
    variables: {
      legacyId: universityId || "",
      ...paginationVariables
    },
    skip: !universityId,
    errorPolicy: "all"
  });

  const connection = data?.universities?.[0]?.rosterUploads;
  const items = useMemo(
    () =>
      mapRosterUploadRows((connection?.nodes ?? []).filter((node): node is UniversityRosterUploadNode => node != null)),
    [connection?.nodes]
  );
  const columns = useMemo(() => getColumns(), []);

  if (error && !connection) {
    return <EmptyState title="Unable to Load Roster Uploads" caption="Try refreshing the page." iconName="error" />;
  }

  // Wrong/missing university id returns []. Unauthorized viewers still get the university with an
  // empty rosterUploads connection (backend masks uploads), which keeps the empty-uploads state below.
  if (!loading && data && data.universities.length === 0) {
    return <EmptyState title="University Not Found" caption="This university could not be loaded." iconName="error" />;
  }

  return (
    <>
      <TableHeading heading="Past Roster Uploads" body="Download CSV files previously uploaded for this university." />
      <Table
        data={items}
        columns={columns}
        loading={loading}
        data-testid="past-roster-uploads-table"
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Roster Files Uploaded"
            caption="Roster CSVs uploaded for this university will appear here."
            iconName="upload_file"
          />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls pagination={createHeaderPagination(connection, { loading })} onSortClick={onSortClick} />
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(connection, { loading })}
            totalRowCount={connection?.totalCount ?? items.length}
          />
        )}
      />
    </>
  );
}
